import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { UserService } from 'src/app/services/user.service';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'upsc-auth',
  templateUrl: './auth.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit {
  tokenError: boolean = false;
  errorOccurred: boolean = false;
  appStateTarget = '';
  email: string | undefined = '';
  public loading: boolean = false;
  systemError: boolean = false;
  workflow: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private loginService: LoginService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public auth: AuthService
  ) {
    if(this.route.snapshot.queryParams.UID) {
      sessionStorage.setItem('UID', this.route.snapshot.queryParams.UID);
    }
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (claims: IdToken | null | undefined) => {
        if (claims) {
          sessionStorage.setItem('idToken', claims.__raw);
          this.email = claims.email;
          if(sessionStorage.getItem('UID')) {
            this.prepareSSO(sessionStorage.getItem('UID'));
          }
          else {
            this.prepareSSO(claims.sub.split('|')[1]);
          }
        }
      }
    );
  }

  ngOnInit() {
    this.auth.isAuthenticated$.subscribe(
      (isAuthenticated) => {
        if (!isAuthenticated) {
          let queryParams = this.route.snapshot.queryParams;
          let urlFragment = this.route.snapshot.fragment;
          if (queryParams['returnUrl']) {
            sessionStorage.setItem('returnUrl', queryParams['returnUrl']),
              this.router.navigate([], {
                queryParams: {
                  'returnUrl': null,
                },
                queryParamsHandling: 'merge'
              })
          }
          if (queryParams.UID && queryParams.page) {
            //coming from Shopify. send users to passwordless auth0 flow to verify email.
            sessionStorage.setItem('landingPage', queryParams.page);
            this.initiatePasswordlessAuth();
          }
          else if (urlFragment) {
            if (urlFragment.includes('&code=')) {
              this.msalBroadcastService.msalSubject$.pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
                takeUntil(this._destroying$)
              ).subscribe((result: EventMessage) => {
                console.log('Log in succeeded - ', result);
                sessionStorage.setItem('idToken', result.payload['idToken']);
                sessionStorage.setItem('refresh_token', result.payload['refreshToken']);
                this.decodeToken(result.payload['accessToken']);
              });
              this.msalBroadcastService.inProgress$.pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
              ).subscribe(() => {
                if (this.msalService.instance.getAllAccounts()?.length < 1) {
                  this.redirectToB2C();
                }
              })
            }
            else if (urlFragment.includes('error=')) {
              this.handleErrors();
            }
            else {
              this.redirectToB2C();
            }
          }
          else {
            this.redirectToAuth0(); //send to auth0 first to determine if there is an active auth0 session.
          }
        }
      }
    );
  }

  initiatePasswordlessAuth() {
    console.log("initiating");
    this.auth.loginWithRedirect({
      authorizationParams: {
        acr_values: 'onl',
        connection: 'email',
        // login_hint: email,
        redirect_uri: `${environment.baseUrl.web}callback`,
        'ext-connect': 'email', //additional param to indicate template to hide "sign up" and "login" text
        'ext-type': 'hint'
      },
      appState: { target: `/auth` }
    });
  }

  prepareSSO(ID: string, page?: string) {
    // let returnURL = 'login';
    this.userService.getAutoAuthProfile(ID).subscribe(
      res => {
        if(res?.data?.emailAddress?.toLowerCase() != this.email?.toLowerCase()) {
          this.handleErrors();
        }
        if (res?.data?.policyNumber) {
          let profileData = res.data;
          let ssoPage = profileData.page ? profileData.page.toLowerCase() : sessionStorage.getItem('landingPage');
          sessionStorage.setItem('access_token', profileData.accessToken);
          sessionStorage.setItem('userid', profileData.userId);
          delete profileData.accessToken;
          this.userService.setUserInfo(profileData);
          this.userService.setUserPermissions(profileData.permissions);
          sessionStorage.setItem('refresh_token', profileData.refreshToken);
          this.userService.getPolicySearch(profileData.policyNumber).subscribe(
            policyData => {
              if (policyData?.data && policyData?.message?.toLowerCase() == 'succeeded') {
                sessionStorage.setItem('policyDetails', JSON.stringify(policyData.data));
                if(policyData.data.policyTypeIndicator == 'Policy') {
                  this.userService.updateUserInfo({ userRoleID: '5' }); //comming from shopify
                }
                if (policyData.data.policyNumber?.startsWith('6-')) { //commerce guard
                  this.userService.updateUserInfo({ userRoleID: '8' });
                }
                if (profileData.bundleID && profileData.customerNumber && profileData.contractNumber) { //bundled claims. additionalInfo2 - bundleNumber; additionalInfo3 - customerNumber; additionalInfo4 - contractNumber;
                  this.userService.updateUserInfo({ userRoleID: '9' });
                  let contractNumber = profileData.contractNumber;
                  this.userService.getPolicySearch(contractNumber).subscribe(
                    contractData => {
                      if (contractData?.data && contractData?.message?.toLowerCase() == 'succeeded') {
                        sessionStorage.setItem('bundledContractDetails', JSON.stringify(contractData.data));
                        sessionStorage.setItem('bundledPolicyDetails', JSON.stringify(policyData.data));
                      }
                      // returnURL = ssoPage;
                      this.router.navigateByUrl(ssoPage);
                    },
                    error => this.handleErrors()
                  );
                }
                else {
                  // returnURL = ssoPage;
                  this.router.navigateByUrl(ssoPage);
                }
              }
              else {
                this.handleErrors();
              }
            },
            error => {
              this.handleErrors();
            }
          );
        }
        else {
          this.handleErrors();
        }
      },
      err => {
        this.handleErrors();
      }
    );
  }

  redirectToAuth0() {
    this.auth.loginWithRedirect({
      authorizationParams: {
        acr_values: 'onl',
        redirect_uri: `${environment.baseUrl.web}callback`
      },
      appState: { target: `/auth` }
    });
  }

  redirectToB2C() {
    // this.msalService.instance.clearCache();
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  decodeToken(token: string) {
    sessionStorage.setItem('access_token', token)
    this.router.navigateByUrl('authorize');
  }

  handleErrors() {
    sessionStorage.clear();
    this.router.navigate(['auth-error']);
  }

  private readonly _destroying$ = new Subject<void>();
  ngOnDestroy(): void {
    sessionStorage.removeItem('UID');
    sessionStorage.removeItem('landingPage');
    this.msalService.instance.clearCache();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  emailAddress = '';
  showCodeVerification = false;
  checkVerification(data) {
  }
}
